import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Link2, Pencil, Plus, Scale, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  actualizarB2bGrupo,
  borrarB2bGrupo,
  crearB2bGrupo,
  getB2bGrupos,
  getB2bInventario,
  getContratos,
  type B2bGrupo,
  type B2bMiembro,
  type ContratoListItem,
} from "@/lib/api";

const PRODUCTO_COLOR: Record<string, string> = {
  "Energía": "bg-amber-100 text-amber-800",
  "Potencia": "bg-indigo-100 text-indigo-800",
  "CEL": "bg-emerald-100 text-emerald-800",
  "i-REC": "bg-sky-100 text-sky-800",
};

function cardinalidad(nCompras: number, nVentas: number): string {
  const c = nCompras === 1 ? "1" : "n";
  const v = nVentas === 1 ? "1" : "n";
  return `${c}:${v}`;
}

function fechaCorta(s: string | null | undefined): string {
  return s ? s.slice(0, 10) : "—";
}

export function B2bPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const qContratos = useQuery({ queryKey: ["contratos"], queryFn: getContratos });
  const qGrupos = useQuery({ queryKey: ["b2b-grupos"], queryFn: getB2bGrupos });

  const contratos = useMemo(() => qContratos.data?.items ?? [], [qContratos.data]);
  const grupos = useMemo(() => qGrupos.data?.items ?? [], [qGrupos.data]);
  const porId = useMemo(
    () => new Map(contratos.map((c) => [c.id_contrato, c])),
    [contratos],
  );
  // contrato → membresías existentes (un híbrido puede tener varias: una por pata)
  const membresiasDe = useMemo(() => {
    const m = new Map<number, { g: B2bGrupo; producto: string | null }[]>();
    for (const g of grupos)
      for (const mb of g.miembros ?? [])
        m.set(mb.id_contrato, [...(m.get(mb.id_contrato) ?? []), { g, producto: mb.producto }]);
    return m;
  }, [grupos]);

  // ---- editor (crear / editar) ----
  const [editando, setEditando] = useState<number | null>(null); // id_grupo o null = nuevo
  const [balanza, setBalanza] = useState<B2bGrupo | null>(null); // grupo abierto en la balanza
  const [nombre, setNombre] = useState("");
  const [notas, setNotas] = useState("");
  const [sel, setSel] = useState<Set<number>>(new Set());
  // pata que entra al grupo, por contrato seleccionado (solo híbridos);
  // null/ausente = el contrato completo.
  const [patas, setPatas] = useState<Record<number, string | null>>({});

  const limpiar = () => {
    setEditando(null);
    setNombre("");
    setNotas("");
    setSel(new Set());
    setPatas({});
  };
  const cargarGrupo = (g: B2bGrupo) => {
    setEditando(g.id_grupo);
    setNombre(g.nombre);
    setNotas(g.notas ?? "");
    setSel(new Set(g.contratos));
    setPatas(Object.fromEntries((g.miembros ?? []).map((m) => [m.id_contrato, m.producto])));
    setError(null);
    setAviso(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const toggleSel = (id: number) =>
    setSel((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
        setPatas((p) => {
          const { [id]: _fuera, ...resto } = p;
          return resto;
        });
      } else {
        n.add(id);
        // si alguna pata del contrato ya está tomada en otro grupo, arranca
        // preseleccionando la primera pata libre (no puede entrar completo)
        const c = porId.get(id);
        const tomadas = (membresiasDe.get(id) ?? [])
          .filter((m) => m.g.id_grupo !== editando)
          .map((m) => m.producto);
        if (tomadas.length > 0 && (c?.productos?.length ?? 0) > 1) {
          const libre = (c?.productos ?? []).find((p) => !tomadas.includes(p));
          if (libre) setPatas((p) => ({ ...p, [id]: libre }));
        }
      }
      return n;
    });

  const selCompras = [...sel].filter((id) => porId.get(id)?.rol_mpp === "comprador");
  const selVentas = [...sel].filter((id) => porId.get(id)?.rol_mpp === "vendedor");
  const valido = nombre.trim().length > 0 && selCompras.length >= 1 && selVentas.length >= 1;

  const invalidar = () => qc.invalidateQueries({ queryKey: ["b2b-grupos"] });

  const mGuardar = useMutation({
    mutationFn: () => {
      const body = {
        nombre: nombre.trim(),
        notas: notas.trim() || null,
        contratos: [...sel],
        // explícito para TODOS los seleccionados: null = contrato completo
        productos: Object.fromEntries([...sel].map((id) => [id, patas[id] ?? null])),
      };
      return editando == null ? crearB2bGrupo(body) : actualizarB2bGrupo(editando, body);
    },
    onSuccess: () => {
      setAviso(editando == null ? "Grupo B2B creado." : "Grupo B2B actualizado.");
      setError(null);
      limpiar();
      invalidar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Error al guardar el grupo"),
  });

  const mBorrar = useMutation({
    mutationFn: (id: number) => borrarB2bGrupo(id),
    onSuccess: () => {
      setAviso("Grupo borrado — sus contratos quedaron sin asignar.");
      setError(null);
      if (editando != null) limpiar();
      invalidar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Error al borrar el grupo"),
  });

  const cargando = qContratos.isLoading || qGrupos.isLoading;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-slate-500" /> Back-to-Back
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Asigna los contratos de compra a sus ventas espejo. Un grupo puede calzar 1:1, 1:n,
          n:1 o n:n; cada <b>pata</b> de un contrato pertenece a lo sumo a un grupo — un
          híbrido (p. ej. 001MPP/RIC) puede ir con su energía a un grupo y su potencia a otro.
        </p>
      </div>

      {aviso && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">{aviso}</div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {(qContratos.isError || qGrupos.isError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudo cargar: {((qContratos.error || qGrupos.error) as Error)?.message}
        </div>
      )}

      {/* ---- Editor: nuevo grupo / editar ---- */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
            <Link2 className="w-4 h-4 text-slate-500" />
            {editando == null ? "Nuevo grupo B2B" : `Editando grupo #${editando}`}
          </div>
          <label className="text-sm flex-1 min-w-[16rem]">
            <span className="block text-xs text-slate-500 mb-1">Nombre del grupo</span>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="p. ej. Potencia Xoxocotla 2028–2037"
              className="border rounded px-2 py-1.5 text-sm w-full"
            />
          </label>
          <label className="text-sm flex-[2] min-w-[16rem]">
            <span className="block text-xs text-slate-500 mb-1">Notas (opcional)</span>
            <input
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="margen, condiciones, riesgos…"
              className="border rounded px-2 py-1.5 text-sm w-full"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!valido || mGuardar.isPending}
              onClick={() => mGuardar.mutate()}
              title={valido ? "" : "Nombre + al menos una compra y una venta"}
              className="inline-flex items-center gap-1.5 text-sm bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              {mGuardar.isPending ? "Guardando…" : editando == null ? "Crear grupo" : "Guardar cambios"}
            </button>
            {(editando != null || sel.size > 0 || nombre) && (
              <button
                type="button"
                onClick={limpiar}
                className="inline-flex items-center gap-1 text-sm border rounded px-3 py-1.5 text-slate-600 hover:bg-slate-50"
              >
                <X className="w-4 h-4" /> Cancelar
              </button>
            )}
          </div>
        </div>
        <div className="text-xs text-slate-500">
          Selección: <b className="text-indigo-700">{selCompras.length} compra{selCompras.length === 1 ? "" : "s"}</b>
          {" ↔ "}
          <b className="text-amber-700">{selVentas.length} venta{selVentas.length === 1 ? "" : "s"}</b>
          {selCompras.length > 0 && selVentas.length > 0 && (
            <span className="ml-2 inline-flex text-[11px] font-semibold bg-slate-100 rounded-full px-2 py-0.5">
              {cardinalidad(selCompras.length, selVentas.length)}
            </span>
          )}
        </div>

        {cargando ? (
          <div className="text-sm text-slate-500">Cargando contratos…</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <ListaContratos
              titulo="Compras (MPP comprador)"
              color="indigo"
              contratos={contratos.filter((c) => c.rol_mpp === "comprador")}
              sel={sel}
              patas={patas}
              onPata={(id, p) => setPatas((prev) => ({ ...prev, [id]: p }))}
              membresiasDe={membresiasDe}
              editando={editando}
              onToggle={toggleSel}
            />
            <ListaContratos
              titulo="Ventas (MPP vendedor)"
              color="amber"
              contratos={contratos.filter((c) => c.rol_mpp === "vendedor")}
              sel={sel}
              patas={patas}
              onPata={(id, p) => setPatas((prev) => ({ ...prev, [id]: p }))}
              membresiasDe={membresiasDe}
              editando={editando}
              onToggle={toggleSel}
            />
          </div>
        )}
      </div>

      {/* ---- Grupos existentes ---- */}
      <div className="space-y-3">
        <div className="text-sm font-medium text-slate-700">
          Grupos asignados{grupos.length > 0 ? ` (${grupos.length})` : ""}
        </div>
        {qGrupos.isLoading ? (
          <div className="text-sm text-slate-500">Cargando…</div>
        ) : grupos.length === 0 ? (
          <div className="bg-white border rounded-lg p-6 text-sm text-slate-500">
            Aún no hay grupos Back-to-Back. Selecciona compras y ventas arriba y crea el primero.
          </div>
        ) : (
          grupos.map((g) => (
            <TarjetaGrupo
              key={g.id_grupo}
              g={g}
              porId={porId}
              onBalanza={() => setBalanza(g)}
              onEditar={() => cargarGrupo(g)}
              onBorrar={() => {
                if (window.confirm(`¿Borrar el grupo "${g.nombre}"? Los contratos quedan sin asignar.`)) {
                  mBorrar.mutate(g.id_grupo);
                }
              }}
            />
          ))
        )}
      </div>

      {balanza && <BalanzaModal g={balanza} onClose={() => setBalanza(null)} />}
    </div>
  );
}

function ListaContratos({
  titulo,
  color,
  contratos,
  sel,
  patas,
  onPata,
  membresiasDe,
  editando,
  onToggle,
}: {
  titulo: string;
  color: "indigo" | "amber";
  contratos: ContratoListItem[];
  sel: Set<number>;
  patas: Record<number, string | null>;
  onPata: (id: number, producto: string | null) => void;
  membresiasDe: Map<number, { g: B2bGrupo; producto: string | null }[]>;
  editando: number | null;
  onToggle: (id: number) => void;
}) {
  const encabezado = color === "indigo" ? "text-indigo-700" : "text-amber-700";
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className={`px-3 py-2 bg-slate-50 text-xs font-semibold ${encabezado}`}>{titulo}</div>
      <div className="max-h-96 overflow-y-auto divide-y">
        {contratos.map((c) => {
          // membresías en OTROS grupos (las del grupo en edición sí se pueden mover)
          const ajenas = (membresiasDe.get(c.id_contrato) ?? []).filter(
            (m) => m.g.id_grupo !== editando,
          );
          const prods = c.productos ?? [];
          const patasTomadas = ajenas.map((m) => m.producto);
          // bloqueado del todo: ya entró completo a otro grupo, o todas sus
          // patas están repartidas; un híbrido con pata libre sigue disponible
          const bloqueado =
            ajenas.length > 0 &&
            (patasTomadas.includes(null) ||
              prods.length === 0 ||
              prods.every((p) => patasTomadas.includes(p)));
          const marcado = sel.has(c.id_contrato);
          const esHibrido = prods.length > 1;
          return (
            <label
              key={c.id_contrato}
              className={`flex items-start gap-2.5 px-3 py-2 text-sm ${
                bloqueado ? "opacity-45 cursor-not-allowed bg-slate-50" : "cursor-pointer hover:bg-slate-50"
              } ${marcado ? "bg-blue-50/60" : ""}`}
            >
              <input
                type="checkbox"
                checked={marcado}
                disabled={bloqueado}
                onChange={() => onToggle(c.id_contrato)}
                className="w-4 h-4 mt-0.5 accent-blue-600"
              />
              <span className="min-w-0 flex-1">
                <span className="font-medium text-slate-800">{c.folio}</span>
                <span className="text-slate-500"> · {c.contraparte}</span>
                <span className="block text-xs text-slate-400">
                  {prods.map((p) => (
                    <span key={p} className={`inline-flex rounded px-1.5 mr-1 ${PRODUCTO_COLOR[p] ?? "bg-slate-100"}`}>
                      {p}
                    </span>
                  ))}
                  {c.modalidad ?? "—"} · {fechaCorta(c.fecha_inicio)} → {fechaCorta(c.fecha_fin)}
                  {ajenas.map((m) => (
                    <span key={m.g.id_grupo} className="text-slate-500">
                      {" "}· {m.producto ? `pata ${m.producto}` : "ya"} en “{m.g.nombre}”
                    </span>
                  ))}
                </span>
                {/* híbrido seleccionado: elegir qué pata entra a ESTE grupo */}
                {marcado && esHibrido && (
                  <span className="mt-1 flex items-center gap-1.5 text-xs text-slate-600">
                    Pata que entra al grupo:
                    <select
                      value={patas[c.id_contrato] ?? ""}
                      onClick={(e) => e.preventDefault()}
                      onChange={(e) => onPata(c.id_contrato, e.target.value || null)}
                      className="border rounded px-1.5 py-0.5 text-xs"
                    >
                      <option value="" disabled={patasTomadas.length > 0}>
                        Todas las patas
                      </option>
                      {prods.map((p) => (
                        <option key={p} value={p} disabled={patasTomadas.includes(p)}>
                          Solo {p}
                          {patasTomadas.includes(p) ? " (en otro grupo)" : ""}
                        </option>
                      ))}
                    </select>
                  </span>
                )}
              </span>
            </label>
          );
        })}
        {contratos.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-400">Sin contratos con este rol.</div>
        )}
      </div>
    </div>
  );
}

function TarjetaGrupo({
  g,
  porId,
  onBalanza,
  onEditar,
  onBorrar,
}: {
  g: B2bGrupo;
  porId: Map<number, ContratoListItem>;
  onBalanza: () => void;
  onEditar: () => void;
  onBorrar: () => void;
}) {
  // ¿el grupo calza en volumen? (posición neta por producto que calcula el backend)
  const descalzado = (g.posicion ?? []).some((p) => Math.abs(p.neto) > 1e-6);
  const pataDe = new Map((g.miembros ?? []).map((m) => [m.id_contrato, m.producto]));
  const volumenDe = new Map((g.miembros ?? []).map((m) => [m.id_contrato, m.volumen ?? []]));
  const miembros = g.contratos.map((id) => porId.get(id)).filter(Boolean) as ContratoListItem[];
  const compras = miembros.filter((c) => c.rol_mpp === "comprador");
  const ventas = miembros.filter((c) => c.rol_mpp === "vendedor");
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-800">{g.nombre}</span>
        <span className="text-[11px] font-semibold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
          {cardinalidad(compras.length, ventas.length)}
        </span>
        <span className="text-xs text-slate-400">
          {compras.length} compra{compras.length === 1 ? "" : "s"} ↔ {ventas.length} venta{ventas.length === 1 ? "" : "s"}
        </span>
        <div className="ml-auto flex gap-1.5">
          <button
            type="button"
            onClick={onBalanza}
            title={
              descalzado
                ? "Balanza: el grupo NO calza en volumen — ver inventario por periodo"
                : "Balanza: inventario del grupo por periodo (compras vs ventas)"
            }
            className={`p-1 ${descalzado ? "text-amber-600 hover:text-amber-800" : "text-slate-400 hover:text-emerald-700"}`}
          >
            <Scale className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onEditar}
            title="Editar grupo"
            className="text-slate-400 hover:text-blue-700 p-1"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onBorrar}
            title="Borrar grupo (los contratos quedan sin asignar)"
            className="text-slate-400 hover:text-red-600 p-1"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center mt-3">
        <div className="space-y-1">
          {compras.map((c) => (
            <ChipContrato
              key={c.id_contrato}
              c={c}
              color="indigo"
              pata={pataDe.get(c.id_contrato) ?? null}
              volumen={volumenDe.get(c.id_contrato) ?? []}
            />
          ))}
        </div>
        <ArrowLeftRight className="w-4 h-4 text-slate-300 mx-auto hidden md:block" />
        <div className="space-y-1">
          {ventas.map((c) => (
            <ChipContrato
              key={c.id_contrato}
              c={c}
              color="amber"
              pata={pataDe.get(c.id_contrato) ?? null}
              volumen={volumenDe.get(c.id_contrato) ?? []}
            />
          ))}
        </div>
      </div>
      {g.notas && <div className="text-xs text-slate-500 italic mt-2">{g.notas}</div>}
    </div>
  );
}

function ChipContrato({
  c,
  color,
  pata,
  volumen,
}: {
  c: ContratoListItem;
  color: "indigo" | "amber";
  pata: string | null;
  volumen: NonNullable<B2bMiembro["volumen"]>;
}) {
  const cls =
    color === "indigo"
      ? "bg-indigo-50 border-indigo-200 text-indigo-900"
      : "bg-amber-50 border-amber-200 text-amber-900";
  return (
    <div className={`border rounded px-2.5 py-1.5 text-xs ${cls}`}>
      <b>{c.folio}</b>
      {volumen.length > 0 && (
        <span className="font-semibold" title="Cantidad contratada que entra al grupo">
          {" "}[{volumen
            .map((v) => `${nfInv.format(v.cantidad)} ${v.unidad}${v.anios > 1 ? ` × ${v.anios} años` : ""}`)
            .join(" · ")}]
        </span>
      )}{" "}
      · {c.contraparte}
      {pata && (
        <span
          className={`ml-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${PRODUCTO_COLOR[pata] ?? "bg-slate-100"}`}
          title={`Solo la pata de ${pata} de este contrato pertenece al grupo`}
        >
          solo {pata}
        </span>
      )}
      <span className="block opacity-70">
        {(c.productos ?? []).join(" · ") || "—"} · {fechaCorta(c.fecha_inicio)} → {fechaCorta(c.fecha_fin)}
      </span>
    </div>
  );
}

const nfInv = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 });
const fmtFechaInv = (iso: string) =>
  new Date(iso + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });

/** Balanza del grupo: inventario MENSUAL (compras, ventas y neto) del producto,
 *  calculado solo con los contratos del grupo y acotado a las fechas en que operan. */
function BalanzaModal({ g, onClose }: { g: B2bGrupo; onClose: () => void }) {
  const disponibles = (g.posicion ?? []).map((p) => p.producto);
  const [producto, setProducto] = useState<string | null>(disponibles[0] ?? null);
  const q = useQuery({
    queryKey: ["b2b-inventario", g.id_grupo, producto],
    queryFn: () => getB2bInventario(g.id_grupo, producto),
  });
  const d = q.data;
  const tabs = d?.productos_disponibles?.length ? d.productos_disponibles : disponibles;
  const puntos = d?.puntos ?? [];
  const fechasDesde = (d?.contratos ?? []).map((c) => c.vigencia_desde).sort();
  const fechasHasta = (d?.contratos ?? []).map((c) => c.vigencia_hasta).sort();
  const desde = fechasDesde.length ? fechasDesde[0] : null;
  const hasta = fechasHasta.length ? fechasHasta[fechasHasta.length - 1] : null;
  const neto = d?.totales.inventario ?? 0;
  const calzado = Math.abs(neto) < 1e-6;
  const anual = (d?.agrupacion ?? (producto === "Potencia" ? "anio" : "mes")) === "anio";

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              <Scale className="w-4 h-4 text-slate-500" /> Balanza · {g.nombre}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Inventario {anual ? "anual" : "mensual"} con solo los contratos del grupo
              {desde && hasta && (
                <>
                  {" "}· operan del <b className="text-slate-700">{fmtFechaInv(desde)}</b> al{" "}
                  <b className="text-slate-700">{fmtFechaInv(hasta)}</b>
                </>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" title="Cerrar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b">
          {tabs.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setProducto(p)}
              className={`text-xs rounded-full px-2.5 py-1 border ${
                (d?.producto ?? producto) === p
                  ? PRODUCTO_COLOR[p] ?? "bg-slate-100"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {p}
            </button>
          ))}
          {d && (
            <span
              className={`ml-auto text-xs font-semibold rounded-full px-2.5 py-1 ${
                calzado ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"
              }`}
              title="Neto vigente = compras − ventas (volumen por contrato)"
            >
              compra {nfInv.format(d.totales.compras)} · venta {nfInv.format(d.totales.ventas)} · neto{" "}
              {neto > 0 ? "+" : ""}
              {nfInv.format(neto)} {d.unidad_contrato}
              {calzado ? " · calzado" : neto > 0 ? " · largo" : " · corto"}
            </span>
          )}
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {q.isLoading && <div className="text-sm text-slate-500">Calculando inventario…</div>}
          {q.isError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
              {(q.error as Error)?.message}
            </div>
          )}
          {d && puntos.length === 0 && (
            <div className="text-sm text-slate-500">Sin volumen de {d.producto} en los contratos del grupo.</div>
          )}
          {d && puntos.length > 0 && (
            <>
              <div className="text-xs text-slate-500 mb-2">Unidad: {d.unidad}</div>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <ComposedChart data={puntos} barGap={0} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} minTickGap={8} />
                    <YAxis tick={{ fontSize: 12 }} width={64} tickFormatter={(v) => nfInv.format(v as number)} />
                    <Tooltip formatter={(v: number, name: string) => [`${nfInv.format(v)} ${d.unidad}`, name]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="compras" name="Compras" fill="#4f46e5" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="ventas" name="Ventas" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                    <Line
                      type="monotone"
                      dataKey="inventario"
                      name="Inventario (neto)"
                      stroke="#059669"
                      strokeWidth={2.5}
                      dot={puntos.length <= 60 ? { r: 3 } : false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                Inventario positivo = MPP largo (compró más de lo que vendió); negativo = corto. Energía en MWh del
                mes; potencia = capacidad (MW-año) vigente al cierre del año; CEL / i-REC = certificados del mes.
              </p>

              <table className="w-full text-xs mt-3">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-1 pr-2">{anual ? "Año" : "Mes"}</th>
                    <th className="py-1 pr-2 text-right">Compras</th>
                    <th className="py-1 pr-2 text-right">Ventas</th>
                    <th className="py-1 text-right">Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {puntos.map((p) => (
                    <tr key={p.periodo} className="border-b border-slate-100">
                      <td className="py-1 pr-2 text-slate-700">{p.etiqueta}</td>
                      <td className="py-1 pr-2 text-right text-indigo-800">{nfInv.format(p.compras)}</td>
                      <td className="py-1 pr-2 text-right text-amber-800">{nfInv.format(p.ventas)}</td>
                      <td
                        className={`py-1 text-right font-medium ${
                          Math.abs(p.inventario) < 1e-6 ? "text-emerald-700" : p.inventario > 0 ? "text-sky-700" : "text-red-700"
                        }`}
                      >
                        {p.inventario > 0 ? "+" : ""}
                        {nfInv.format(p.inventario)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-3 text-xs text-slate-500">
                Contratos:{" "}
                {d.contratos.map((c, i) => (
                  <span key={c.folio}>
                    {i > 0 && " · "}
                    <span className={c.tipo === "Compra" ? "text-indigo-800" : "text-amber-800"}>
                      {c.folio} {nfInv.format(c.cantidad)} {d.unidad_contrato}
                    </span>{" "}
                    <span className="text-slate-400">
                      ({fmtFechaInv(c.vigencia_desde)} → {fmtFechaInv(c.vigencia_hasta)})
                    </span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
